import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Logout as LogoutIcon,
  Inventory2,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { InventoryPanel } from '../home/InventoryPanel.jsx';

/**
 * Mobile "More" tab — settings, profile, inventory, logout.
 */
export const MobileMoreTab = () => {
  const { logout, outletName, userName } = useAuth();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [showInventory, setShowInventory] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      console.error('Logout failed:', e);
    }
  };

  const menuItems = [
    {
      icon: <Person sx={{ fontSize: 22 }} />,
      label: 'Profile Settings',
      onClick: () => {},
    },
    {
      icon: <Inventory2 sx={{ fontSize: 22 }} />,
      label: 'Inventory',
      onClick: () => setShowInventory((v) => !v),
      active: showInventory,
    },
    {
      icon: <Settings sx={{ fontSize: 22 }} />,
      label: 'App Settings',
      onClick: () => {},
    },
  ];

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
      {/* User info card */}
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          borderRadius: '18px',
          border: '1px solid #C8C8CC',
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {outletName || 'My Restaurant'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
          {userName || 'Staff'}
        </Typography>
      </Box>

      {/* Menu items */}
      {menuItems.map((item, idx) => (
        <Box
          key={idx}
          onClick={item.onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            mb: 0.5,
            bgcolor: item.active ? 'rgba(69, 39, 160, 0.08)' : 'rgba(255, 255, 255, 0.72)',
            borderRadius: '14px',
            border: '1px solid',
            borderColor: item.active ? '#4527A0' : '#C8C8CC',
            cursor: 'pointer',
            transition: 'all 0.15s',
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          <Box sx={{ color: item.active ? '#4527A0' : '#86868B' }}>{item.icon}</Box>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
            {item.label}
          </Typography>
        </Box>
      ))}

      {/* Inventory panel (expandable) */}
      {showInventory && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <InventoryPanel />
        </Box>
      )}

      {/* Logout button */}
      <Button
        fullWidth
        variant="outlined"
        startIcon={<LogoutIcon />}
        onClick={() => setLogoutDialogOpen(true)}
        sx={{
          mt: 3,
          borderRadius: '14px',
          py: 1.5,
          fontWeight: 600,
          fontSize: '0.88rem',
          borderColor: '#FF3B30',
          color: '#FF3B30',
          textTransform: 'none',
          '&:hover': { bgcolor: 'rgba(255,59,48,0.04)', borderColor: '#FF3B30' },
        }}
      >
        Logout
      </Button>

      {/* Logout confirmation dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '18px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to logout?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button onClick={handleLogout} sx={{ color: '#FF3B30', fontWeight: 600 }}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
